import * as React from "react";
import { useNavigate } from "react-router";
import {
  Checkbox,
  Button,
  Margin,
  P,
  Stack,
  Card,
  Strong,
} from "@ovotech/element";
import { Page, SplitHeading, TextField } from "_core/components";
import { FormApi, Formy } from "_core/form";
import {
  FormValues,
  PersonalDetailsValidator,
} from "_core/validators/PersonalDetails";
import { ProductUrls } from "_core/types";
import { useStore } from "_core/store";
import { useConfig } from "_core/utils/config";
import Mixpanel from "_core/analytics/Mixpanel";
import {
  AnalyticsEvents,
  DataLayerEvent,
} from "_core/analytics/AnalyticsEvents";
import { LoadingSkeleton } from "_core/components/LoadingSkeleton";
import { DataLayer } from "_core/analytics/DataLayer";
import { getPrice } from "_core/utils/charger";
import { useRegisterNewCustomer } from "_core/hooks/useRegisterNewCustomer";
import { JumptechLink } from "_core/components/JumptechLink";
import { PrivacyPolicy } from "_core/components/PrivacyPolicy";

const initialValues: FormValues = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  confirmPhone: "",
  privacyPolicy: false,
};

const PersonalDetailsPageContent = () => {
  const config = useConfig();
  const navigate = useNavigate();
  const { isLoading, registerNewCustomer } = useRegisterNewCustomer();
  const emailRef = React.useRef<HTMLInputElement>(null);
  const phoneRef = React.useRef<HTMLInputElement>(null);

  const { setCustomerDetails, charger, cableSetup, cableLength, baseColour } =
    useStore();

  const handleSubmit = async (values: FormValues) => {
    setCustomerDetails({
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phoneNumber: values.phoneNumber,
    });

    await registerNewCustomer(values);

    Mixpanel.track(AnalyticsEvents.CLICKEDBUTTON, {
      buttonId: "personal-details-submit",
      pageName: ProductUrls.PersonalDetails,
      charger,
      cableSetup,
      privacyPolicy: values.privacyPolicy,
      skipEmailConfirmation: true,
    });
    DataLayer.push({
      event: DataLayerEvent.SubmitPersonalDetails,
      pageName: ProductUrls.PersonalDetails,
      charger,
      cableSetup,
      price: getPrice(charger, cableSetup, cableLength, baseColour),
      skipEmailConfirmation: true,
    });
  };

  return isLoading ? (
    <LoadingSkeleton />
  ) : (
    <>
      <Formy
        initialValues={initialValues}
        validationSchema={PersonalDetailsValidator(config)}
        onSubmit={handleSubmit}
      >
        {({
          handleSubmit,
          values,
          errors,
          touched,
          setFieldTouched,
          setFieldValue,
        }: FormApi<FormValues>) => {
          return (
            <form
              data-testid="personal-details"
              onSubmit={handleSubmit}
              noValidate={true}
            >
              <Stack spaceBetween={10}>
                <Stack spaceBetween={8}>
                  <SplitHeading part1="Get your" part2="installation survey" />

                  <P size={"lg"}>
                    We need to confirm your details, so we can send you a link
                    to your installation survey. It’ll take you around 20 to 30
                    minutes to fill in – and we’ll send it to you by text
                    message. The survey gives us the details we need to issue
                    your final quote and successfully install a charger at your
                    home.
                  </P>
                </Stack>

                <Card className={"ContainerCard"}>
                  <TextField
                    required
                    label="First name"
                    id="first-name"
                    name="firstName"
                    value={values.firstName}
                  />
                  <TextField
                    required
                    label="Last name"
                    id="last-name"
                    name="lastName"
                    value={values.lastName}
                  />
                  <TextField
                    required
                    ref={emailRef}
                    label="Email"
                    id="email"
                    name="email"
                    value={values.email}
                  />
                  <TextField
                    required
                    ref={phoneRef}
                    label="Mobile number"
                    hint="We’ll text your survey link to this number."
                    id="phoneNumber"
                    name="phoneNumber"
                    value={values.phoneNumber}
                  />
                  <TextField
                    required
                    label="Confirm mobile number"
                    id="confirmPhone"
                    name="confirmPhone"
                    value={values.confirmPhone}
                  />
                  <Margin top={1} />
                  <Checkbox
                    data-testid="privacyPolicy"
                    name="privacyPolicy"
                    id="privacyPolicy"
                    label={
                      <Strong>
                        I agree to OVO’s <PrivacyPolicy />
                      </Strong>
                    }
                    checked={values.privacyPolicy}
                    error={touched.privacyPolicy && errors.privacyPolicy}
                    onBlur={() => setFieldTouched("privacyPolicy")}
                    onChange={() =>
                      setFieldValue("privacyPolicy", !values.privacyPolicy)
                    }
                  />
                  <P className={"SurfaceVariant"}>
                    By ticking this box, you’re agreeing to share your details
                    with us and <JumptechLink />, our trusted EV charger
                    installation partner.
                  </P>
                </Card>
                <div className={"ButtonContainer"}>
                  <Button
                    variant="outline"
                    iconLeft="chevron-left"
                    data-testid="personal-details-back-button"
                    onClick={() => navigate(-1)}
                  >
                    Back
                  </Button>
                  <Button
                    type={"submit"}
                    iconRight="chevron-right"
                    data-testid="personal-details-submit-button"
                  >
                    Confirm details
                  </Button>
                </div>
              </Stack>
            </form>
          );
        }}
      </Formy>
      <Margin top={12} />
    </>
  );
};

export const PersonalDetailsPage = () => {
  return (
    <Page displaySteps url={ProductUrls.PersonalDetails}>
      <PersonalDetailsPageContent />
    </Page>
  );
};
